import React, { useEffect, useId, useState, useCallback } from 'react';
import { useAnalysisSubscription } from 'hooks/useAnalysisSubscription';
import { useAnalyzeBrainer } from 'hooks/useAnalyzeBrainer';
import { useAnalyzeV0 } from 'hooks/useAnalyzeV0';
import { useTabActivityStore } from 'store/useTabActivityStore';
import { transformTFCards, transformKeyLevels, splitPrice } from 'utils/brainerCoreTransform';
import { PriceHero } from 'components/analysis/PriceHero';
import { BrainWavesChart } from 'components/analysis/BrainWavesChart';
import { TimeframeCard } from 'components/analysis/TimeframeCard';
import { KeyLevels } from 'components/analysis/KeyLevels';
import { AnalysisView } from 'components/analysis/AnalysisView';
import { AnalysisTrace } from 'components/analysis/AnalysisTrace';
import { ExchangeMarketSelect } from 'components/selectors/ExchangeMarketSelect';
import { PairSelect } from 'components/selectors/PairSelect';
import { TimeframeSelect } from 'components/selectors/TimeframeSelect';
import { EngineSelect } from 'components/selectors/EngineSelect';
import { Skeleton } from 'components/common/Skeleton';
import { ElapsedTimer } from 'components/common/ElapsedTimer';
import { ErrorCard } from 'components/common/ErrorCard';
import { ErrorBoundary } from 'components/common/ErrorBoundary';
import { ImageDropzone } from 'components/common/ImageDropzone';
import { DEFAULT_ENGINE, canUseEngine, engineLabel, engineBadgeLabel, type Engine } from 'api/analysisApi';
import { useAuthStore } from 'store/useAuthStore';
import 'components/analysis/analysis.css';
import './AnalysisPanel.css';

type Exchange = 'binance' | 'mexc';
type Market = 'spot' | 'futures';
type ViewMode = 'data' | 'analysis';
type AnalysisSub = 'trace' | 'result';

interface AnalysisPanelProps {
	onRemove?: () => void;
	removable?: boolean;
	// Drag-to-reorder grip, supplied by the sortable wrapper. Lives in the header
	// so dragging never collides with the panel's own interactive controls.
	dragHandle?: React.ReactNode;
}

const MAX_AUTO_RETRIES = 2;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const VIEW_TABS: { value: ViewMode; label: string }[] = [
	{ value: 'data', label: 'Data' },
	{ value: 'analysis', label: 'Analysis' },
];

export function AnalysisPanel({ onRemove, removable = true, dragHandle }: AnalysisPanelProps) {
	const [exchange, setExchange] = useState<Exchange>('mexc');
	const [market, setMarket] = useState<Market>('futures');
	const [pair, setPair] = useState('BTC/USDT');
	const [tf, setTf] = useState('30m');
	const [view, setView] = useState<ViewMode>('data');
	const [sub, setSub] = useState<AnalysisSub>('trace');
	const user = useAuthStore((s) => s.user);
	// v1 is the preferred starting engine, but only for accounts allowed to run it.
	const [engine, setEngine] = useState<Engine>(() =>
		canUseEngine(useAuthStore.getState().user, 'brainer_v1') ? 'brainer_v1' : DEFAULT_ENGINE,
	);
	// If access changes under an open panel (sign-in as another account), snap a
	// now-forbidden selection back to the default rather than letting it 403.
	useEffect(() => {
		if (!canUseEngine(user, engine)) setEngine(DEFAULT_ENGINE);
	}, [user, engine]);

	const [openMenu, setOpenMenu] = useState<'pair' | 'tf' | 'engine' | null>(null);
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);

	const assetParam = pair.replace('/', '');
	const dataMarket: Market = exchange === 'mexc' ? 'spot' : market;

	const { data: inner, isLoading, isTimeout, retry } = useAnalysisSubscription(assetParam, tf, dataMarket, exchange);

	// v0 and v0.1 are the image-upload engines (v0.1 = v0 + DB heatmap); everything
	// else (incl. v1) runs through the asset/tf analyze-brainer path.
	const isV0 = engine === 'brainer_v0' || engine === 'brainer_v0_1';
	const isV0Heatmap = engine === 'brainer_v0_1';

	const ai = useAnalyzeBrainer(assetParam, tf, exchange, engine);
	// v0 / v0.1 (BrainerPro + Claude) is image-driven, so it lives in its own runner.
	// Pass `tf` so v0.1 selects the matching heatmap product (<1h intraday, 1h+ swing).
	const v0 = useAnalyzeV0(exchange, isV0Heatmap, tf);
	const [v0Files, setV0Files] = useState<File[]>([]);

	// Report this panel's manual-analysis activity so the tab title spinner and
	// favicon dot can reflect overall progress. Cleared on unmount so removing a
	// panel mid-run drops its contribution. Live WS subscription loading is
	// intentionally excluded; 'aborted' is treated as neutral (no error flash).
	const panelId = useId();
	const setActivity = useTabActivityStore((s) => s.setActivity);
	const clearActivity = useTabActivityStore((s) => s.clearActivity);
	useEffect(() => {
		const running = ai.status === 'loading' || v0.status === 'loading';
		const errored = ai.status === 'error' || v0.status === 'error';
		const succeeded = ai.status === 'success' || v0.status === 'success';
		const activity = running ? 'running' : errored ? 'error' : succeeded ? 'success' : 'idle';
		setActivity(panelId, activity);
	}, [panelId, ai.status, v0.status, setActivity]);
	useEffect(() => () => clearActivity(panelId), [panelId, clearActivity]);

	// The Analysis tab does NOT auto-run — the user picks an engine first, then
	// hits Run. (v0 has always been manual since it needs an uploaded screenshot.)

	// Switching engine or exchange clears any staged upload / v0 result so a stale
	// screenshot or analysis never carries over.
	const v0Reset = v0.reset;
	useEffect(() => {
		v0Reset();
		setV0Files([]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [exchange, engine]);

	// ── Auto-retry the live sub on timeout ──
	const [retryCount, setRetryCount] = useState(0);
	useEffect(() => {
		setRetryCount(0);
	}, [assetParam, tf, dataMarket, exchange]);
	useEffect(() => {
		if (!isTimeout) return;
		if (retryCount < MAX_AUTO_RETRIES) {
			retry();
			setRetryCount((c) => c + 1);
		}
	}, [isTimeout, retry, retryCount]);
	const hasExhaustedRetries = isTimeout && retryCount >= MAX_AUTO_RETRIES;

	const openPair = useCallback((el: HTMLElement) => {
		setAnchor(el);
		setOpenMenu((m) => (m === 'pair' ? null : 'pair'));
	}, []);
	const openTf = useCallback((el: HTMLElement) => {
		setAnchor(el);
		setOpenMenu((m) => (m === 'tf' ? null : 'tf'));
	}, []);
	const openEngine = useCallback((el: HTMLElement) => {
		setAnchor(el);
		setOpenMenu((m) => (m === 'engine' ? null : 'engine'));
	}, []);
	const closeMenu = useCallback(() => setOpenMenu(null), []);

	const dropdowns = (
		<>
			<PairSelect open={openMenu === 'pair'} anchor={anchor} selected={pair} onSelect={setPair} onClose={closeMenu} />
			<TimeframeSelect open={openMenu === 'tf'} anchor={anchor} selected={tf} exchange={exchange} onSelect={setTf} onClose={closeMenu} />
			<EngineSelect open={openMenu === 'engine'} anchor={anchor} selected={engine} onSelect={setEngine} onClose={closeMenu} />
		</>
	);

	const header = (
		<div className='panel-header'>
			<div className='panel-header-row'>
				<div className='panel-header-left'>
					<ExchangeMarketSelect exchange={exchange} market={market} onChangeExchange={setExchange} onChangeMarket={setMarket} />
					{dragHandle}
				</div>
				{removable && (
					<button className='panel-remove' type='button' onClick={onRemove} title='Remove panel'>
						✕
					</button>
				)}
			</div>
			<div className='panel-header-row'>
				<div className='header-switches'>
					<div className='view-switch seg'>
						{VIEW_TABS.map((t) => (
							<button key={t.value} type='button' className={`seg-btn${view === t.value ? ' on' : ''}`} onClick={() => setView(t.value)}>
								{t.label}
							</button>
						))}
					</div>
					{/* Analysis-only: badge that names the active A/B calc engine and opens the picker. */}
					{view === 'analysis' && (
						<button type='button' className={`engine-badge${openMenu === 'engine' ? ' open' : ''}`} onClick={(e) => openEngine(e.currentTarget)} title='Analysis engine (A/B)'>
							<span className='engine-badge-text'>{engineBadgeLabel(engine)}</span>
							<span className='engine-badge-caret'>▾</span>
						</button>
					)}
				</div>
				<span className='panel-target'>
					{pair} · {tf}
				</span>
			</div>
		</div>
	);

	// ── Body ──
	let body: React.ReactNode;
	if (view === 'analysis' && isV0) {
		// BrainerPro + Claude (v0 / v0.1): image-driven. Upload → Claude vision → prose
		// (no Enforcer); v0.1 also injects the cached DB liquidity heatmap.
		const v0Item = v0.data?.results?.[0];
		const v0Tag = `v0_${assetParam}_${tf}_${exchange}`;
		if (v0.status === 'loading') {
			body = (
				<div className='panel-center'>
					<div className='spinner' />
					<div className='center-msg'>Running {isV0Heatmap ? 'v0.1' : 'v0'} analysis…</div>
					<div className='center-sub'>
						Direct Claude vision · <strong>{engineLabel(engine)}</strong>
						{isV0Heatmap ? ' · + DB liquidity heatmap' : ' · no Enforcer'}, up to a minute
					</div>
					<ElapsedTimer className='elapsed-timer' />
					<button className='retry-btn' type='button' onClick={() => v0.abort()}>
						✕ Stop
					</button>
				</div>
			);
		} else if (v0.status === 'success' && v0Item) {
			body = (
				<>
					<div className='analysis-subbar'>
						<div className='view-switch seg'>
							<button type='button' className={`seg-btn${sub === 'trace' ? ' on' : ''}`} onClick={() => setSub('trace')}>
								Data View
							</button>
							<button type='button' className={`seg-btn${sub === 'result' ? ' on' : ''}`} onClick={() => setSub('result')}>
								Graphic View
							</button>
						</div>
						<button className='block-btn' type='button' onClick={() => v0.run()}>
							↻ Re-run
						</button>
					</div>
					{sub === 'result' ? (
						<AnalysisView result={v0Item} livePrice={inner?.price ?? null} />
					) : v0Item.debug ? (
						<AnalysisTrace trace={v0Item.debug} tag={v0Tag} engine={engine} />
					) : (
						<div className='panel-center'>
							<div className='center-msg'>No trace returned</div>
							<div className='center-sub'>The backend didn't include a debug trace.</div>
						</div>
					)}
				</>
			);
		} else {
			// idle / error / aborted → the upload form
			body = (
				<div className='panel-center'>
					<div className='center-msg'>{engineLabel(engine)}</div>
					<div className='center-sub'>Upload Brainer Pro screenshot(s) — direct Claude vision, no Enforcer{isV0Heatmap ? ', plus the cached DB liquidity heatmap.' : '.'}</div>
					<ImageDropzone files={v0Files} onFilesChange={setV0Files} />
					{v0Files.length > 0 && (
						<div className='center-sub'>
							{v0Files.length} file{v0Files.length > 1 ? 's' : ''} ready
						</div>
					)}
					{v0.status === 'error' && <div className='center-sub'>{v0.error}</div>}
					<button className='retry-btn' type='button' disabled={v0Files.length === 0} onClick={() => v0.run(v0Files)}>
						{v0.status === 'aborted' ? '↻ Run again' : '▶ Run analysis'}
					</button>
				</div>
			);
		}
	} else if (view === 'analysis') {
		const item = ai.data?.results?.[0];
		const tag = `${assetParam}_${tf}_${exchange}`;
		if (ai.status === 'idle') {
			// Wait for an explicit Run — the user chooses the engine (badge above) first.
			body = (
				<div className='panel-center'>
					<div className='center-msg'>Ready to analyze</div>
					<div className='center-sub'>
						{assetParam} · {tf} · {cap(exchange)} · <strong>{engineLabel(engine)}</strong>
						<br />
						Pick an engine above, then run.
					</div>
					<button className='retry-btn' type='button' onClick={ai.run}>
						▶ Run analysis
					</button>
				</div>
			);
		} else if (ai.status === 'loading') {
			body = (
				<div className='panel-center'>
					<div className='spinner' />
					<div className='center-msg'>Running deep analysis…</div>
					<div className='center-sub'>
						{assetParam} · {tf} · {cap(exchange)} · <strong>{engineLabel(engine)}</strong> — full pipeline, up to a minute
					</div>
					<ElapsedTimer className='elapsed-timer' />
					<button className='retry-btn' type='button' onClick={ai.abort}>
						✕ Stop
					</button>
				</div>
			);
		} else if (ai.status === 'aborted') {
			body = (
				<div className='panel-center'>
					<div className='center-msg'>Analysis stopped</div>
					<div className='center-sub'>You cancelled this run.</div>
					<button className='retry-btn' type='button' onClick={ai.run}>
						↻ Run again
					</button>
				</div>
			);
		} else if (ai.status === 'error') {
			body = (
				<div className='panel-center'>
					<div className='center-msg'>Analysis failed</div>
					<div className='center-sub'>{ai.error}</div>
					<button className='retry-btn' type='button' onClick={ai.run}>
						↻ Try again
					</button>
				</div>
			);
		} else if (item) {
			body = (
				<>
					<div className='analysis-subbar'>
						<div className='view-switch seg'>
							<button type='button' className={`seg-btn${sub === 'trace' ? ' on' : ''}`} onClick={() => setSub('trace')}>
								Data View
							</button>
							<button type='button' className={`seg-btn${sub === 'result' ? ' on' : ''}`} onClick={() => setSub('result')}>
								Graphic View
							</button>
						</div>
						<button className='block-btn' type='button' onClick={ai.run}>
							↻ Re-run
						</button>
					</div>
					{sub === 'result' ? (
						<AnalysisView result={item} livePrice={inner?.price ?? null} />
					) : item.debug ? (
						<AnalysisTrace trace={item.debug} tag={tag} engine={engine} />
					) : (
						<div className='panel-center'>
							<div className='center-msg'>No trace returned</div>
							<div className='center-sub'>The backend didn't include a debug trace (mock mode, or debug=true unsupported).</div>
						</div>
					)}
				</>
			);
		} else {
			body = (
				<div className='panel-center'>
					<div className='center-msg'>No analysis returned</div>
					<button className='retry-btn' type='button' onClick={ai.run}>
						↻ Try again
					</button>
				</div>
			);
		}
	} else if (!inner && (isLoading || (isTimeout && !hasExhaustedRetries))) {
		// Skeleton only when there's nothing to show. Once we have data, keep it on
		// screen through any same-key reload (reconnect / auto-retry / refresh) and
		// let it swap silently when the next push lands — no blink. A fresh key
		// (new pair/TF/market/exchange) nulls `inner`, so genuine switches still
		// surface the skeleton.
		body = <Skeleton pair={pair} timeframe={tf} exchange={cap(exchange)} market={cap(dataMarket)} />;
	} else if (!inner) {
		body = (
			<ErrorCard
				pair={pair}
				timeframe={tf}
				attempts={MAX_AUTO_RETRIES}
				onRetry={() => {
					setRetryCount(0);
					retry();
				}}
				onOpenPair={openPair}
				onOpenTf={openTf}
			/>
		);
	} else {
		const tfCards = transformTFCards(inner);
		const keyLevels = transformKeyLevels(inner);
		const { major, cents } = splitPrice(inner.price);
		const isBull = inner.ctf_trend === 'BULLISH';
		const metaLine = [inner.exchange, inner.asset_type].filter(Boolean).join(' · ');

		body = (
			<>
				<PriceHero metaLine={metaLine || `${cap(exchange)} · ${cap(dataMarket)}`} pair={pair} timeframe={tf} priceMajor={major} priceCents={cents} isBull={isBull} onOpenPair={openPair} onOpenTf={openTf} />
				<div className='panel-grid'>
					<div className='pg-item'>
						<BrainWavesChart data={inner.brainwaves?.ctf} timeframe={inner.ctf_tf} bwValue={inner.bw_current} bwDir={inner.bw_current_dir} />
					</div>
					{tfCards.map((card, i) => (
						<div className='pg-item' key={i}>
							<TimeframeCard data={card} isLast />
						</div>
					))}
					<div className='pg-item'>
						<KeyLevels resistance={keyLevels.resistance} support={keyLevels.support} />
					</div>
				</div>
			</>
		);
	}

	return (
		<div className='analysis-panel'>
			{header}
			<div className='panel-body'>
				<ErrorBoundary>{body}</ErrorBoundary>
			</div>
			{dropdowns}
		</div>
	);
}
